import { useCustom } from '@refinedev/core';
import { Card, Col, Row, Statistic, Typography } from 'antd';
import {
  ClockCircleOutlined,
  EyeInvisibleOutlined,
  RiseOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { UserStatsOut } from '@/api/generated/schemas';

const pct = (part: number, whole: number) =>
  whole ? Math.round((part / whole) * 100) : 0;

// 5 cards: 1 / 2 / 3 per row, all in one row from xl up
const colProps = { xs: 24, sm: 12, lg: 8, xl: { flex: '20%' } } as const;

export const UserKpiCards = () => {
  const { query } = useCustom<UserStatsOut>({
    url: 'iam/users/stats/',
    method: 'get',
  });

  const stats = query.data?.data;
  const regular = stats ? stats.verified + stats.unverified : 0;
  const verifiedPct = stats ? pct(stats.verified, regular) : 0;
  const anonymousPct = stats ? pct(stats.anonymous, stats.total_users) : 0;

  return (
    <Row gutter={[16, 16]}>
      <Col {...colProps}>
        <Card loading={query.isLoading}>
          <Statistic
            title="Użytkownicy ogółem"
            value={stats?.total_users ?? 0}
            prefix={<UserOutlined />}
            suffix={
              stats?.new_users.today ? (
                <Typography.Text
                  type="success"
                  style={{ fontSize: 12, marginLeft: 4 }}
                >
                  +{stats.new_users.today} dziś
                </Typography.Text>
              ) : undefined
            }
          />
        </Card>
      </Col>

      <Col {...colProps}>
        <Card loading={query.isLoading}>
          <Statistic
            title="Nowi (30 dni)"
            value={stats?.new_users.last_30_days ?? 0}
            prefix={<RiseOutlined />}
            valueStyle={{ color: '#3f8600' }}
            suffix={
              stats?.new_users.last_7_days != null ? (
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 12, marginLeft: 4 }}
                >
                  7d: {stats.new_users.last_7_days}
                </Typography.Text>
              ) : undefined
            }
          />
        </Card>
      </Col>

      <Col {...colProps}>
        <Card loading={query.isLoading}>
          <Statistic
            title="Użytkownicy zarejestrowani"
            value={regular}
            prefix={<TeamOutlined />}
            valueStyle={{ color: '#1677ff' }}
            suffix={
              stats ? (
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 12, marginLeft: 4 }}
                >
                  {verifiedPct}% zweryf.
                </Typography.Text>
              ) : undefined
            }
          />
        </Card>
      </Col>

      <Col {...colProps}>
        <Card loading={query.isLoading}>
          <Statistic
            title="Użytkownicy anonimowi"
            value={stats?.anonymous ?? 0}
            prefix={<EyeInvisibleOutlined />}
            suffix={
              stats ? (
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 12, marginLeft: 4 }}
                >
                  {anonymousPct}%
                </Typography.Text>
              ) : undefined
            }
          />
        </Card>
      </Col>

      <Col {...colProps}>
        <Card loading={query.isLoading}>
          <Statistic
            title="Do usunięcia"
            value={stats?.pending_deletion ?? 0}
            prefix={<ClockCircleOutlined />}
            valueStyle={{
              color: (stats?.pending_deletion ?? 0) > 0 ? '#cf1322' : undefined,
            }}
          />
        </Card>
      </Col>
    </Row>
  );
};
